/**
 * The in-game clock.
 *
 * Minutes since midnight, plus a day number. Minutes rather than seconds
 * because everything the game reasons about is scheduled in minutes — shift
 * starts, BEO deadlines, how late is late — and storing seconds would mean
 * every one of those comparisons carrying a `* 60`.
 *
 * `scale` is game minutes per real second, and the default of 1 makes one real
 * minute an hour of hotel time. That is the number the whole feel of the game
 * hangs off: an eight hour shift is eight real minutes, a three hour BEO window
 * is three real minutes of actual sprinting, and a deadline is something you
 * can watch arrive. Faster and the shift is over before you have crossed the
 * property; slower and you are waiting on a clock instead of playing.
 *
 * Pure. No timers, no rAF — it is stepped with a delta like everything else, so
 * a test can run a whole week through it in a millisecond.
 */

export const MINUTES_PER_DAY = 24 * 60;

/** The scales the player can cycle through, slowest first. */
export const SCALES = Object.freeze([0, 0.25, 0.5, 1, 2, 4, 10, 30]);

export const DAY_NAMES = Object.freeze([
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]);

export class TimeConfigError extends Error {}

/** "14:30" to 870. Throws rather than returning NaN, so bad data fails at boot. */
export function parseTime(text) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(text).trim());
  if (!match) throw new TimeConfigError(`"${text}" is not a HH:MM time`);
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) throw new TimeConfigError(`"${text}" is not a real time`);
  return hours * 60 + minutes;
}

/** 870 to "2:30 PM". Twelve hour, because that is what a hotel schedule reads. */
export function formatTime(minutes) {
  const wrapped = ((Math.floor(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours24 = Math.floor(wrapped / 60);
  const mins = wrapped % 60;
  const suffix = hours24 < 12 ? 'AM' : 'PM';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hours12}:${String(mins).padStart(2, '0')} ${suffix}`;
}

/** 870 to "14:30". What schedule data is written in. */
export function formatClock(minutes) {
  const wrapped = ((Math.floor(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

/** "1h 20m", for a countdown. Negative reads as overdue. */
export function formatSpan(minutes) {
  const sign = minutes < 0 ? '-' : '';
  const total = Math.abs(Math.round(minutes));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return hours ? `${sign}${hours}h ${mins}m` : `${sign}${mins}m`;
}

export function createClock({ minutes = 0, day = 1, scale = 1 } = {}) {
  let now = minutes;
  let today = day;
  let rate = scale;
  const listeners = new Set();

  function rollover() {
    let rolled = 0;
    while (now >= MINUTES_PER_DAY) {
      now -= MINUTES_PER_DAY;
      today += 1;
      rolled += 1;
    }
    return rolled;
  }

  return {
    get minutes() {
      return now;
    },
    get day() {
      return today;
    },
    get scale() {
      return rate;
    },
    set scale(value) {
      rate = Math.max(0, value);
    },
    get paused() {
      return rate === 0;
    },
    /** Monday for day 1, so the week reads the way a schedule does. */
    get dayName() {
      return DAY_NAMES[(today - 1) % 7];
    },

    /** Advances by real seconds. Returns how many midnights went past. */
    update(dt) {
      if (rate === 0) return 0;
      now += dt * rate;
      const rolled = rollover();
      if (rolled) for (const listener of listeners) listener(today);
      return rolled;
    },

    /**
     * Jumps forward to a time of day, rolling into tomorrow if that time has
     * already been and gone. This is how sleeping works, and why it cannot
     * accidentally run the clock backwards.
     */
    advanceTo(targetMinutes, { atLeast = 0 } = {}) {
      const before = today * MINUTES_PER_DAY + now;
      let after = today * MINUTES_PER_DAY + targetMinutes;
      while (after < before + atLeast) after += MINUTES_PER_DAY;
      today = Math.floor(after / MINUTES_PER_DAY);
      now = after % MINUTES_PER_DAY;
      for (const listener of listeners) listener(today);
      return after - before;
    },

    /** Steps a fixed number of minutes, for debugging. */
    skip(minutes) {
      now += minutes;
      rollover();
    },

    /** Next slower / faster scale from the fixed ladder. */
    slower() {
      const index = nearestScale(rate);
      rate = SCALES[Math.max(index - 1, 0)];
      return rate;
    },
    faster() {
      const index = nearestScale(rate);
      rate = SCALES[Math.min(index + 1, SCALES.length - 1)];
      return rate;
    },

    onNewDay(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    format: () => formatTime(now),
    toJSON: () => ({ minutes: now, day: today, scale: rate }),
    load(state) {
      if (!state) return;
      now = state.minutes ?? now;
      today = state.day ?? today;
      rate = state.scale ?? rate;
    },
  };
}

function nearestScale(rate) {
  let best = 0;
  for (let i = 1; i < SCALES.length; i += 1) {
    if (Math.abs(SCALES[i] - rate) < Math.abs(SCALES[best] - rate)) best = i;
  }
  return best;
}
